import { isPlainObject, isString } from 'remeda'
import topbar from 'topbar'
import { type Cleanup, composeCleanup } from './helpers/cleanup'
import { type LivePropsSync, livePropsSync } from './helpers/live-props'
import { enhancedS3Uploader } from './helpers/utils'
import { loadPageComponent } from './helpers/webawesome-components'

// Private types ---------------------------------------------------------------

type InputEventDetail = Readonly<{
  cursor?: unknown
  id: string
}>

// Public types ----------------------------------------------------------------

export type FacadeDomCallbacks = Pick<
  LivePropsSync,
  'onPatchStart' | 'onBeforeElUpdated' | 'onNodeAdded' | 'onPatchEnd'
>

export type {
  GridRenderContext,
  GridRenderReporter
} from './hooks/grid/utils/render-timing'
export type { NavigationMeasurement } from './navigation-timing'

// Constants -------------------------------------------------------------------

export const facadeUploaders = { S3: enhancedS3Uploader } as const

// Private functions -----------------------------------------------------------

// Parses the id shared by focus and clear LiveView events.
const inputEventDetail = (event: Event): InputEventDetail | undefined => {
  const detail: unknown =
    event instanceof CustomEvent ? event.detail : undefined

  return isPlainObject(detail) && isString(detail.id)
    ? { cursor: detail.cursor, id: detail.id }
    : undefined
}

// Parses the URL carried by a LiveView download event.
const downloadUrl = (event: Event): string | undefined => {
  const detail: unknown =
    event instanceof CustomEvent ? event.detail : undefined

  return isPlainObject(detail) && isString(detail.url) ? detail.url : undefined
}

// Leaves editor-owned focus and clearing to the editor's hook.
const findTextInput = (
  id: string
): HTMLInputElement | HTMLTextAreaElement | undefined => {
  const element = document.getElementById(id)
  const input =
    element?.localName === 'wa-input'
      ? element.shadowRoot?.querySelector('input')
      : element

  return input instanceof HTMLInputElement ||
    input instanceof HTMLTextAreaElement
    ? input
    : undefined
}

// Focuses the editable text control requested by LiveView.
const handleFocusInput = (event: Event): void => {
  const detail = inputEventDetail(event)

  if (!detail) {
    return
  }

  const el = findTextInput(detail.id)

  if (!el) {
    return
  }

  el.focus()

  if (detail.cursor === 'end') {
    el.setSelectionRange(el.value.length, el.value.length)
  } else if (detail.cursor === 'start') {
    el.setSelectionRange(0, 0)
  }
}

// Lets the normal input handler carry the cleared value back to the form.
const handleClearInput = (event: Event): void => {
  const detail = inputEventDetail(event)

  if (!detail) {
    return
  }

  const el = findTextInput(detail.id)

  if (!el) {
    return
  }

  el.value = ''
  el.dispatchEvent(new Event('input', { bubbles: true, composed: true }))
}

// Opens a server-requested download without navigating the current page.
const handleDownload = (event: Event): void => {
  const url = downloadUrl(event)

  if (url) {
    window.open(url, '_blank')
  }
}

// Connects global UI commands for the lifetime of the application integration.
const setupCommands = (): Cleanup => {
  window.addEventListener('phx:focus-input', handleFocusInput)
  window.addEventListener('phx:clear-input', handleClearInput)
  window.addEventListener('phx:download', handleDownload)

  return () => {
    window.removeEventListener('phx:focus-input', handleFocusInput)
    window.removeEventListener('phx:clear-input', handleClearInput)
    window.removeEventListener('phx:download', handleDownload)
  }
}

// Acknowledges navigation immediately while avoiding flashes on fast patches.
const handleLoadingStart = (event: Event): void => {
  const detail: unknown =
    event instanceof CustomEvent ? event.detail : undefined
  const isPatch = isPlainObject(detail) && detail.kind === 'patch'

  topbar.show(isPatch ? 500 : 0)
}

// Hides the progress bar when LiveView finishes navigation.
const handleLoadingStop = (): void => {
  topbar.hide()
}

// Owns progress listeners and cancels a pending show when released.
const setupProgressBar = (): Cleanup => {
  topbar.config({
    className: 'progressbar',
    barThickness: 2,
    shadowBlur: 0,
    shadowColor: 'transparent',
    barColors: { 0: '#4263eb' }
  })

  window.addEventListener('phx:page-loading-start', handleLoadingStart)
  window.addEventListener('phx:page-loading-stop', handleLoadingStop)

  return () => {
    window.removeEventListener('phx:page-loading-start', handleLoadingStart)
    window.removeEventListener('phx:page-loading-stop', handleLoadingStop)
    topbar.hide()
  }
}

// Public functions ------------------------------------------------------------

export * as FacadeHooks from './hooks'
export { setGridRenderReporter } from './hooks/grid/utils/render-timing'
export { setupNavigationTiming } from './navigation-timing'

// Composes the shared property engine and lazy controls into Phoenix's patch callbacks.
export const createFacadeDomCallbacks = (
  reportComponentError: (error: unknown) => void
): FacadeDomCallbacks => ({
  onPatchStart: (container) => livePropsSync.onPatchStart(container),
  onBeforeElUpdated: (from, to) => livePropsSync.onBeforeElUpdated(from, to),
  onNodeAdded: (node) => {
    livePropsSync.onNodeAdded(node)
    loadPageComponent(node)?.catch(reportComponentError)
  },
  onPatchEnd: (container) => livePropsSync.onPatchEnd(container)
})

// Connects Facade once per document before LiveSocket connects; the caller owns cleanup.
export const setupFacadeLiveView = (): Cleanup =>
  composeCleanup(setupProgressBar(), setupCommands(), livePropsSync.connect())
